"use client";

// HOW IT WORKS WITH YOU — the one choice of identities, wherever something new
// is named: an agent being made, a connection's name, a key's mint form, an
// agent's settings (MOVING-IN-PLAN.md §8).
//
// WORDS AND NAMES (25 oct, John: "change it to identities"): what a person reads is
// "identity" and "identities"; what the code and the wire call these stays "personality"
// (this file, usePersonalities, personalities.css, the `personalities` field the agents
// api takes, `identity_mix`), so nothing breaks. The words here say identities.
//
// Chips of the person's identities. A tap chooses one; the order of the taps
// is the order they are mixed in (compose composeIdentities: the later one says
// it more precisely), and a chosen chip carries its place when there are several.
// "write a new one" makes one in place, with a name and how it talks, and chooses
// it once it is kept (a list only holds identities the server already has, and the
// new one is for no tool and not every agent's: only where it is chosen does an ai
// read it, compose MIX_ONLY). A thing nothing was chosen for shows what it reads today (its tool's own
// identity, else the general one); that is not written unless the person
// changes it, so a later identity for its tool still reaches it.
//
// Saving is quiet (lib/identity/client): a choice is written the moment the thing
// it is for exists (`target` arrives), and written again until it lands. There is
// no "did not save" anywhere: at worst the line under the chips says "saving…".
// Hidden entirely for whoever identities are not open to (the routes answer
// 404, F.agentIdentity).

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Field } from "../ui";
import { idsShown, mixOnlyIdentity, pickIdentity } from "../../lib/identity/compose";
import { retryQuietly, saveMixQuietly } from "../../lib/identity/client";
import { MAX_IDENTITIES, MAX_MIX, type AgentIdentity } from "../../lib/identity/types";
import { usePersonalities, type PersonalitiesState } from "./usePersonalities";
import "./personalities.css";

type Shared = ReturnType<typeof usePersonalities>;

/** the general one: the first for every agent that says something, else the first for every agent */
export function generalOf(list: readonly AgentIdentity[]): AgentIdentity | null {
  return pickIdentity(list, null) ?? list.find((i) => i.for.length === 0) ?? null;
}

/** save the whole list of identities with one more in it, until it lands; resolves with the server's list */
export function saveNewPersonality(list: readonly AgentIdentity[], fresh: AgentIdentity): Promise<AgentIdentity[]> {
  const next = [...list, fresh];
  return new Promise((resolve) => {
    retryQuietly(
      async () => {
        const r = await fetch("/api/me/agent-identities", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identities: next, known: idsShown(next) }),
        });
        if (!r.ok) throw new Error(String(r.status));
        return (((await r.json()) as { identities?: AgentIdentity[] }).identities ?? next) as AgentIdentity[];
      },
      { onLanded: resolve },
    );
  });
}

/**
 * Keep a new identity, and only once the server has it hand it to `choose`: a list of identities only holds ones the
 * server already knows, so a choice sent beside the keeping could arrive first and be left out of the list, while the chip
 * said it was chosen. `kept` hears the server's list as soon as it lands. Nothing is chosen that did not land.
 */
export async function keepThenChoose(
  list: readonly AgentIdentity[],
  fresh: AgentIdentity,
  o: { kept: (landed: AgentIdentity[]) => void; choose: () => void; save?: typeof saveNewPersonality },
): Promise<void> {
  const landed = await (o.save ?? saveNewPersonality)(list, fresh);
  o.kept(landed);
  if (landed.some((i) => i.id === fresh.id)) o.choose();
}

export default function PersonalityPicker({
  target,
  value,
  onChange,
  client = null,
  inheritFrom = null,
  generalByDefault = false,
  label = "how it works with you",
  state,
}: {
  /** "agent:<id>" · "connection:<id>" · "key:<id>": what the choice is saved for; null until it exists */
  target: string | null;
  /** controlled (the make step sends it with the make): the list, in order. Nothing is saved here then */
  value?: string[];
  onChange?: (ids: string[]) => void;
  /** the tool it is for, so an untouched choice shows what it reads today */
  client?: string | null;
  /** a target whose list it starts from when it has none (a sub-agent: its parent's) */
  inheritFrom?: string | null;
  /** the general one chosen when nothing else is (a new agent) */
  generalByDefault?: boolean;
  label?: string;
  /** the host's own read, when it has one already */
  state?: Shared | PersonalitiesState;
}) {
  const own = usePersonalities();
  const st = (state ?? own) as Shared;
  const groupId = useId();
  const [touched, setTouched] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [writing, setWriting] = useState(false);
  const [name, setName] = useState("");
  const [talks, setTalks] = useState("");
  const waiting = useRef<string[] | null>(null);
  // what the last render chose and saves with, for a write that finishes after the person went on choosing
  const latest = useRef<{ chosen: string[]; set: (next: string[]) => void } | null>(null);

  const list = st.list;
  const stored = target ? st.mix?.mix[target] : undefined;
  const base = useMemo(() => {
    if (stored?.length) return stored;
    const inherited = inheritFrom ? st.mix?.mix[inheritFrom] : undefined;
    if (inherited?.length) return inherited;
    if (generalByDefault) {
      const g = generalOf(list);
      return g ? [g.id] : [];
    }
    const today = pickIdentity(list, client);
    return today ? [today.id] : [];
  }, [stored, inheritFrom, st.mix, generalByDefault, list, client]);
  const known = new Set(list.map((i) => i.id));
  const chosen = (value ?? touched ?? base).filter((id) => known.has(id));

  // a controlled host learns the default once it is known, so what it sends is what the chips show
  const told = useRef(false);
  useEffect(() => {
    if (!value || told.current || st.loading || !st.open) return;
    told.current = true;
    if (!value.length && base.length) onChange?.(base);
  }, [value, base, st.loading, st.open, onChange]);

  // a choice made before the thing existed is written the moment it does
  useEffect(() => {
    if (!target || value || !waiting.current) return;
    const ids = waiting.current;
    waiting.current = null;
    setSaving(true);
    void saveMixQuietly(target, ids).then(() => setSaving(false));
  }, [target, value]);

  const set = (next: string[]) => {
    const ids = next.slice(0, MAX_MIX);
    if (value) return onChange?.(ids);
    setTouched(ids);
    onChange?.(ids);
    if (!target) {
      waiting.current = ids;
      return;
    }
    setSaving(true);
    void saveMixQuietly(target, ids).then(() => setSaving(false));
  };
  useEffect(() => {
    latest.current = { chosen, set };
  });

  if (st.loading || !st.open) return null;

  const toggle = (id: string) => set(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);

  const write = async () => {
    const n = name.trim().slice(0, 60);
    if (!n || list.length >= MAX_IDENTITIES) return;
    const fresh = { ...mixOnlyIdentity(new Date().toISOString(), n), talks: talks.trim().slice(0, 800) };
    setWriting(false);
    setName("");
    setTalks("");
    // kept first, then chosen (keepThenChoose); what the person chose meanwhile is not undone
    setSaving(true);
    await keepThenChoose(list, fresh, {
      kept: (landed) => {
        setSaving(false);
        st.setList(landed);
      },
      choose: () => {
        const now = latest.current;
        if (now) now.set([...now.chosen, fresh.id]);
      },
    });
  };

  const line =
    saving
      ? "saving…"
      : chosen.length > 1
        ? "mixed in this order: the later one says it more precisely."
        : chosen.length === 0
          ? // an agent is handed an identity only when one is chosen for it; a connection or a key falls back to its tool's, then the general one
            target?.startsWith("agent:") || generalByDefault
            ? "none chosen: it reads none of them."
            : "none chosen: it reads the one made for its tool, then the one for every agent."
          : null;

  return (
    <div className="pp" data-noswipe>
      <p className="pp-label" id={groupId}>
        {label}
      </p>
      <div className="chip-group pp-chips" role="group" aria-labelledby={groupId}>
        {list.map((i) => {
          const at = chosen.indexOf(i.id);
          return (
            <button key={i.id} type="button" className="chip" data-on={at >= 0 || undefined} aria-pressed={at >= 0} onClick={() => toggle(i.id)}>
              {i.name}
              {at >= 0 && chosen.length > 1 ? <span className="pp-n" aria-label={`mixed ${at + 1} of ${chosen.length}`}>{at + 1}</span> : null}
            </button>
          );
        })}
        {list.length < MAX_IDENTITIES && !writing ? (
          <button type="button" className="chip chip-plus" onClick={() => setWriting(true)}>
            write a new one
          </button>
        ) : null}
      </div>
      {writing ? (
        // not a form: the picker often stands inside one (a name step, a mint form), and Enter here keeps only this
        <div
          className="pp-new"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            e.stopPropagation();
            void write();
          }}
        >
          <Field label="its name" value={name} maxLength={60} autoFocus placeholder="terse" onChange={(e) => setName(e.target.value)} />
          <Field label="how it talks, in a line" value={talks} maxLength={800} placeholder="short answers, the answer first. no hype." onChange={(e) => setTalks(e.target.value)} />
          <div className="pp-new__acts">
            <button type="button" className="quiet-link" disabled={!name.trim()} onClick={() => void write()}>
              keep it
            </button>
            <button type="button" className="quiet-link pp-quiet" onClick={() => setWriting(false)}>
              never mind
            </button>
          </div>
        </div>
      ) : null}
      {line ? (
        <p className="pp-meta" role="status">
          {line}
        </p>
      ) : null}
    </div>
  );
}
